/**
 * Queues an outbound OncoEMR task reply via the backend outbox.
 * @module replyOncoemrTask
 */

import type {
  ReplyOncoemrTaskRequest,
  ReplyOncoemrTaskResponse,
} from "../../data-model/oncoemrTasksModel";
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { formatErrorDetail } from "../throwApiError";
import { getBearerToken } from "./bearerToken";

/**
 * Builds the URL for `POST /oncoemr-tasks/{task_row_id}/reply`.
 *
 * @param taskRowId - Primary key of the `tasks` row (`tasks.id`).
 * @returns Absolute request URL.
 */
export const buildOncoemrTaskReplyUrl = (taskRowId: string): string =>
  `${API_ENDPOINTS.ONCOEMR_TASKS}/${encodeURIComponent(taskRowId)}/reply`;

/**
 * Enqueues an outbound reply; OncoEMR delivery happens on the next sync cycle.
 *
 * @param taskRowId - `tasks.id` for the selected list row.
 * @param body - Org scope, requesting user, idempotency key, and message body.
 * @param signal - Optional abort signal.
 * @returns Outbox idempotency key, pending status, and user-facing confirmation text.
 * @throws Error When the task is missing (404) or the request otherwise fails.
 */
export const replyOncoemrTask = async (
  taskRowId: string,
  body: ReplyOncoemrTaskRequest,
  signal?: AbortSignal,
): Promise<ReplyOncoemrTaskResponse> => {
  const bearerToken = await getBearerToken();

  const response = await fetch(buildOncoemrTaskReplyUrl(taskRowId), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${bearerToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    ...(signal && { signal }),
  });

  if (response.status !== 202) {
    const errorData: unknown = await response.json().catch(() => ({}));
    const message = formatErrorDetail(errorData, response.statusText);
    throw new Error(
      `Failed to queue OncoEMR task reply (${response.status}): ${message}`,
    );
  }

  return (await response.json()) as ReplyOncoemrTaskResponse;
};
